/* eslint-disable no-underscore-dangle */
import moment from 'moment-timezone';
import BrowserStorage from '../../services/BrowserStorage';
import StorageKeys from '../../enums/StorageKeys';

const FavouriteStore = new BrowserStorage(StorageKeys.Favourites);

// helpers
function isObsolete(match)
{
    const { state, status } = match;

    if (status === 'finished')
    {
        return true;
    }

    if (status === 'active' || status === 'block')
    {
        if (state === 'prematch')
        {
            // apparently match can be active but finished, we do a time check
            const currentTime = moment.utc().valueOf();
            const matchTime = moment.utc(match.matchDate).valueOf();
            return matchTime < currentTime;
        }
        return false;
    }

    return true;
}

export default {
    namespaced: true,

    state: {
        showFavourites: {
            icon: true,
            menu: true,
        },
        favourites: [],
        subscriptionHandler: null,
    },

    getters: {
        getFavourites(state)
        {
            return state.favourites;
        },
        getFavouritesByOffer(state)
        {
            const offer = state.favourites.reduce((offers, match) =>
            {
                offers[match.state] = offers[match.state] || [];
                offers[match.state].push(match);
                return offers;
            }, {});
            return offer;
        },
        favouritesOnCurrentOffer(state, getters, rootState, rootGetters)
        {
            return getters.getFavouritesByOffer[rootGetters.sportState] || [];
        },
        favouriteMatchesIn(state, getters)
        {
            return getters.favouritesOnCurrentOffer.reduce((matches, match) =>
            {
                matches[match.matchId] = match; return matches;
            }, {});
        },
        favouriteMatchesByTournaments(state, getters)
        {
            const favMatchByTournaments = {};
            getters.favouritesOnCurrentOffer.forEach((fav) =>
            {
                if (!favMatchByTournaments[fav.tournamentId])
                {
                    favMatchByTournaments[fav.tournamentId] = {};
                }
                favMatchByTournaments[fav.tournamentId][fav.matchId] = fav.increment;
            });

            return favMatchByTournaments;
        },
        hasLiveFavourites(state, getters)
        {
            return getters.getFavouritesByOffer.live && getters.getFavouritesByOffer.live.length;
        },
        hasPrematchFavourites(state, getters)
        {
            return getters.getFavouritesByOffer.prematch && getters.getFavouritesByOffer.prematch.length;
        },
        hasFavourites(state)
        {
            return state.favourites && state.favourites.length;
        },
        showFavourites(state)
        {
            return state.showFavourites;
        },
        showFavouritesOnIcon(state)
        {
            return state.showFavourites.icon;
        },
        showFavouritesOnMenu(state)
        {
            return state.showFavourites.menu;
        },
    },

    mutations: {
        setFavourites(state, favourites)
        {
            state.favourites = favourites;
            FavouriteStore.setItem(state.favourites);
        },
        addFavourite(state, match)
        {
            state.favourites.push(match);
            FavouriteStore.setItem(state.favourites);
        },
        removeFavourite(state, match)
        {
            state.favourites = state.favourites.filter((favMatch) => favMatch.matchId !== match.matchId);
            FavouriteStore.setItem(state.favourites);
        },
        updateFavourite(state, match)
        {
            if (!state.favourites.find((fav) => (fav.matchId === match.matchId)))
            {
                return;
            }
            state.favourites = state.favourites.map((fav) => (fav.matchId === match.matchId ? match : fav));
            FavouriteStore.setItem(state.favourites);
        },
        clearFavourites(state)
        {
            state.favourites = [];
            FavouriteStore.removeItem();
        },
        setSubscriptionHandler(state, subscription)
        {
            state.subscriptionHandler = subscription;
        },
        setShowFavourites(state, { key, value })
        {
            state.showFavourites[key] = value;
        },
    },

    actions: {
        async initFavourites({ commit, dispatch })
        {
            if (await FavouriteStore.hasItem())
            {
                commit('setFavourites', await FavouriteStore.getItem());
            }

            await dispatch('fetchFavouritesFromOffer');

            dispatch('subscribeToMatches');
        },
        async fetchFavouritesFromOffer({
            dispatch, commit, getters, rootGetters,
        })
        {
            if (!getters.hasFavourites)
            {
                return;
            }
            let offer;
            try
            {
                offer = await rootGetters.offerHandling.callOffer('/sport/getMatchesByIds',
                    {
                        matchIds: getters.getFavourites.map((match) => match.matchId).join(','),
                        minoffer: '1',
                    });
            }
            catch (e)
            {
                console.log(e);
                dispatch('clearFavourites');
                return;
            }

            if (!offer || offer.error)
            {
                dispatch('clearFavourites');
                return;
            }

            const { data: matches } = offer;

            const favourites = matches.filter((match) => !isObsolete(match));

            commit('setFavourites', favourites);
        },
        subscribeToMatches({
            state, commit, getters, dispatch,
        })
        {
            if (!getters.hasFavourites || state.subscriptionHandler)
            {
                return;
            }

            const unsubscribe = this.subscribeAction((action) =>
            {
                const { type, payload: match } = action;

                if (type === 'setMatch')
                {
                    if (isObsolete(match))
                    {
                        dispatch('removeFavourite', match);
                    }
                    else
                    {
                        dispatch('updateFavourite', match);
                    }
                }
            });

            commit('setSubscriptionHandler', unsubscribe);
        },
        unsubscribeFromMatches({
            state, commit, getters,
        })
        {
            if (getters.hasFavourites)
            {
                return;
            }
            state.subscriptionHandler();
            commit('setSubscriptionHandler', null);
        },
        async addFavourite({ commit, dispatch }, match)
        {
            commit('addFavourite', match);
            dispatch('subscribeToMatches');
        },
        async removeFavourite({ dispatch, commit }, match)
        {
            commit('removeFavourite', match);
            dispatch('unsubscribeFromMatches');
        },
        async updateFavourite({ commit }, match)
        {
            commit('updateFavourite', match);
        },
        async clearFavourites({ commit })
        {
            commit('clearFavourites');
        },
        showFavouritesOnMenu({ commit }, show)
        {
            commit('setShowFavourites', { key: 'menu', value: show });
        },
    },
};
